import { useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Fade,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  ContentCopy,
  Person,
  SmartToy,
} from "@mui/icons-material";
import { renderMarkdown } from "../../../utils/renderMarkdown";

export default function MessageBubble({ msg, isLast }) {
  const [copied, setCopied] = useState(false);
  const isAI = msg.role === "assistant";

  const copy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isAI ? "row" : "row-reverse",
          alignItems: "flex-end",
          gap: 1,
          mb: 1.5,
          px: 1,
        }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: isAI ? "primary.main" : "grey.400",
            flexShrink: 0,
            mb: 0.5,
          }}
        >
          {isAI ? <SmartToy sx={{ fontSize: 16 }} /> : <Person sx={{ fontSize: 16 }} />}
        </Avatar>

        <Box sx={{ maxWidth: "78%", minWidth: 60 }}>
          <Paper
            elevation={0}
            sx={{
              px: 1.75,
              py: 1.25,
              borderRadius: isAI ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
              bgcolor: isAI ? "background.paper" : "primary.main",
              color: isAI ? "text.primary" : "primary.contrastText",
              border: isAI ? "1px solid" : "none",
              borderColor: "divider",
              position: "relative",
              "&:hover .copy-btn": { opacity: 1 },
            }}
          >
            {msg.isLoading ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.25 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "text.secondary",
                      animation: "pulse 1.2s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                      "@keyframes pulse": {
                        "0%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
                        "50%": { opacity: 1, transform: "scale(1)" },
                      },
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                  "& strong": { fontWeight: 700 },
                }}
              >
                {renderMarkdown(msg.content)}
              </Typography>
            )}

            {isAI && !msg.isLoading && false && (
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton
                  className="copy-btn"
                  size="small"
                  onClick={copy}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.2s",
                    position: "absolute",
                    top: 4,
                    right: 4,
                    p: 0.25,
                  }}
                >
                  {copied ? <Check sx={{ fontSize: 12 }} /> : <ContentCopy sx={{ fontSize: 12 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Paper>

          {isAI && isLast && msg.chips && msg.chips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75} justifyContent="flex-start">
              {msg.chips.map((chip, i) => (
                <Chip
                  key={i}
                  label={chip.label}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={chip.onClick}
                  sx={{ fontSize: "0.7rem", height: 24, cursor: "pointer" }}
                />
              ))}
            </Stack>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.25, px: 0.5, textAlign: isAI ? "left" : "right" }}>
            {new Date(msg.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
}

